/**
 * WalletHeaderButton — Web3 wallet indicator in the navbar.
 *
 * States:
 *   1. Not logged in: grey icon + "Connect" (click shows sign-in hint)
 *   2. Logged in, no wallet: grey icon + "Connect Wallet" (click opens MetaMask)
 *   3. Logged in, wallet connected: green dot + truncated address + dropdown
 *
 * Uses wagmi hooks (WagmiProvider wraps the app in index.jsx).
 * Does NOT auto-connect on load — only passive eth_accounts check.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useConnect, useDisconnect } from 'wagmi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWallet } from '../contexts/WalletContext';
import '../styles/WalletHeaderButton.css';

export default function WalletHeaderButton() {
  const { user } = useAuth();
  const { connectedAddress, isWagmiConnected, connectedIsLinked, walletsLoading } = useWallet();
  const { connect, connectors } = useConnect();
  const { disconnect } = useDisconnect();
  const navigate = useNavigate();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [signInHint, setSignInHint] = useState(false);
  const dropdownRef = useRef(null);

  const truncate = (addr) => addr ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : '';

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [dropdownOpen]);

  const handleConnect = () => {
    if (!user) {
      setSignInHint(true);
      setTimeout(() => setSignInHint(false), 3000);
      return;
    }
    const connector = connectors[0];
    if (connector) connect({ connector });
  };

  const handleCopy = async () => {
    if (!connectedAddress) return;
    try {
      await navigator.clipboard.writeText(connectedAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard may fail in some contexts */ }
  };

  const handleDisconnect = () => {
    disconnect();
    setDropdownOpen(false);
  };

  const handleManageWallets = () => {
    setDropdownOpen(false);
    navigate('/profile?tab=wallet');
  };

  // State 3: Connected
  if (isWagmiConnected && connectedAddress) {
    return (
      <div className="wallet-header" ref={dropdownRef}>
        <button
          className="wallet-header-btn connected"
          onClick={() => setDropdownOpen(!dropdownOpen)}
          aria-label="Wallet menu"
        >
          <svg className="wallet-icon" viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
            <path d="M17 6h-1V5a2 2 0 00-2-2H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2v-1h1a1 1 0 001-1V7a1 1 0 00-1-1zm-3 9H4V5h10v10zm2-3h-1v-4h1v4z"/>
          </svg>
          <span className="wallet-dot connected" />
          <span className="wallet-addr-text">{truncate(connectedAddress)}</span>
        </button>

        {dropdownOpen && (
          <div className="wallet-dropdown">
            <button className="wallet-dropdown-address" onClick={handleCopy} title="Click to copy">
              <code>{connectedAddress}</code>
              <span className="copy-hint">{copied ? '✓ Copied' : 'Copy'}</span>
            </button>

            <div className="wallet-dropdown-meta">
              <span className="chain-badge">Base</span>
              {!walletsLoading && connectedIsLinked && <span className="linked-badge">✓ Linked</span>}
              {!walletsLoading && !connectedIsLinked && isWagmiConnected && (
                <span className="unlinked-warning">
                  Not linked —{' '}
                  <button className="link-inline-btn" onClick={handleManageWallets}>
                    Add it
                  </button>
                </span>
              )}
            </div>

            <div className="wallet-dropdown-actions">
              <button className="wallet-dropdown-item" onClick={handleManageWallets}>
                Manage Wallets
              </button>
              <button className="wallet-dropdown-item danger" onClick={handleDisconnect}>
                Disconnect
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // State 1 & 2: Not connected
  return (
    <div className="wallet-header" ref={dropdownRef}>
      <button
        className="wallet-header-btn disconnected"
        onClick={handleConnect}
        aria-label="Connect wallet"
      >
        <svg className="wallet-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" width="16" height="16">
          <path d="M17 6h-1V5a2 2 0 00-2-2H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2v-1h1a1 1 0 001-1V7a1 1 0 00-1-1zm-3 9H4V5h10v10zm2-3h-1v-4h1v4z"/>
        </svg>
        <span className="wallet-dot disconnected" />
        <span className="wallet-connect-text">{user ? 'Connect Wallet' : 'Connect'}</span>
      </button>

      {signInHint && (
        <div className="wallet-signin-hint">
          Sign in to connect your wallet
          <button className="hint-link" onClick={() => { setSignInHint(false); navigate('/login'); }}>
            Log In
          </button>
        </div>
      )}
    </div>
  );
}
